import Vue from "vue";
import Component from "vue-class-component";
import moment from "moment/moment";
import { DataSet } from "@/charts/graphData";

@Component
export default class TotalGraphMixin extends Vue {
  public caLabels = { p1: "", p2: "", p3: "" };
  public caValue = { p1: 0, p2: 0, p3: 0, compare: 0 };
  public titre: string = "";
  /**
   * methode permettant de calculer la progression du CA / la veille/mois precdent/année précédente
   * @returns le taux de progression
   */
  public calculateProgresison(): number {
    if (this.caValue.p1 === 0 || this.caValue.p2 === null) {
      if (this.caValue.p1 !== 0) {
        this.caValue.compare = 100;
      } else {
        this.caValue.compare = 0;
      }
    } else {
      this.caValue.compare = (this.caValue.p1 * 100) / this.caValue.p2;
    }
    return this.caValue.compare;
  }
  /**
   * méthode de mise en forme des periodes et du titre selon les options
   * @param myOptions option du graphique
   * @param titre le titre à mettre en forme
   */
  public orderConfig(myOptions: any, titre?: string) {
    let graphTitle = titre ? titre + " - " : "";
    switch (myOptions.periode) {
      case "J":
        graphTitle += this.$t("j");
        this.caLabels.p1 = moment(myOptions.date)
          .locale(this.$i18n.locale)
          .format("ddd D MMM");
        this.caLabels.p2 = moment(myOptions.date)
          .locale(this.$i18n.locale)
          .subtract(1, "day")
          .format("ddd D MMM");
        this.caLabels.p3 = moment(myOptions.date)
          .locale(this.$i18n.locale)
          .subtract(1, "month")
          .format("ddd D MMM");
        break;
      case "MOIS":
        graphTitle += this.$t("mois");
        this.caLabels.p1 = moment(myOptions.date)
          .locale(this.$i18n.locale)
          .format("MMM YYYY");
        this.caLabels.p2 = moment(myOptions.date)
          .locale(this.$i18n.locale)
          .subtract(1, "month")
          .format("MMM YYYY");
        this.caLabels.p3 = moment(myOptions.date)
          .locale(this.$i18n.locale)
          .subtract(1, "year")
          .format("MMM YYYY");
        break;
      case "ANNEE":
        graphTitle += this.$t("annee");
        this.caLabels.p1 = moment(myOptions.date)
          .locale(this.$i18n.locale)
          .format("YYYY");
        this.caLabels.p2 = moment(myOptions.date)
          .locale(this.$i18n.locale)
          .subtract(1, "year")
          .format("YYYY");
        // this.caLabels.p3 = moment(this.myOptions.date).subtract(1, "year").format("MMM YYY");
        break;
    }
    this.titre = graphTitle;
  }
  /**
   * mise a jour du titre avec la periode
   * @param myOptions options de configuration du graph
   * @param graphTitle le titre du graphique
   */
  public orderTitre(myOptions: any, graphTitle: string): any {
    graphTitle += " - ";
    switch (myOptions.periode) {
      case "J":
        graphTitle += moment(myOptions.date).format("DD MMMM yyyy");
        break;
      case "MOIS":
        graphTitle += moment(myOptions.date).format("MMMM yyyy");
        break;
      case "ANNEE":
        graphTitle += moment(myOptions.date).format("yyyy");
        break;
    }
    this.titre = graphTitle;
  }

  /**
   * methode permettant de compbler les vides pour avoir une valeur par jour sur le mois
   * @param periode la periode
   * @param lignes les lignes a completer
   */
  public makeDataFull(periode: string, lignes: any) {
    if (periode === "MOIS") {
      return this.makeDataFullMois(lignes);
    } else if (periode === "ANNEE") {
      return this.makeDataFullAnnee(lignes);
    } else {
      console.warn("Période non gérée dans makeDataFull:", periode);
      return [[], []];
    }
  }
  /**
   * Méthode pour récupérer les mois sur l'année
   */
  public makeDataFullAnnee(lignes: any) {
    const lignesDatesVente: { [k: string]: any } = {};
    const lignesDatesPresta: { [k: string]: any } = {};

    const mapParMois = new Map<string, number[]>();

    Object.entries(lignes).forEach(([key, value]) => {
      const mois = moment(key).utc().format("YYYY-MM");

      if (!mapParMois.has(mois)) {
        mapParMois.set(mois, [0, 0, 0]);
      }

      const prev = mapParMois.get(mois)!;
      const curr = value as number[];

      mapParMois.set(mois, [
        (prev[0] != null ? prev[0] : 0) + (curr[0] != null ? curr[0] : 0),
        (prev[1] != null ? prev[1] : 0) + (curr[1] != null ? curr[1] : 0),
        (prev[2] != null ? prev[2] : 0) + (curr[2] != null ? curr[2] : 0),
      ]);
    });

    const moisDisponibles = [...mapParMois.keys()].sort();

    const lignesDates: string[] = [];
    const current = moment().startOf("year");

    for (let i = 0; i < 12; i++) {
      const mois = current.clone().add(i, "months");
      const cle = mois.format("YYYY-MM");
      const dateFinale = mois.format("YYYY-MM-01T00:00:00.000+0000");

      const valeurs = mapParMois.get(cle);

      lignesDatesVente[dateFinale] = valeurs ? valeurs[1] : 0;
      lignesDatesPresta[dateFinale] = valeurs ? valeurs[2] : 0;
    }

    return [lignesDatesVente, lignesDatesPresta];
  }
  /**
   * Méthode pour récupérer les mois et les journée
   */
  public makeDataFullMois(lignes: any) {
    const lignesDatesVente: { [k: string]: any } = {};
    const lignesDatesPresta: { [k: string]: any } = {};

    const keys = Object.keys(lignes);
    if (keys.length === 0) {
      return [lignesDatesVente, lignesDatesPresta];
    }

    const premierJour = moment(keys[0]).startOf("month");
    const dernierJour = premierJour.clone().endOf("month");
    const totalJours = dernierJour.diff(premierJour, "days");

    for (let i = 0; i <= totalJours; i++) {
      const jour = premierJour.clone().add(i, "days");
      const cleReelle = Object.keys(lignes).find((dateStr) => {
        const date = moment(dateStr).utc();
        return date.isSame(jour, "day");
      });

      const formaté = jour.format("YYYY-MM-DDT00:00:00.000+0000");

      if (cleReelle && lignes[cleReelle]) {
        const ligne = lignes[cleReelle];
        lignesDatesVente[formaté] = ligne[1];
        lignesDatesPresta[formaté] = ligne[2];
      } else {
        lignesDatesVente[formaté] = 0;
        lignesDatesPresta[formaté] = 0;
      }
    }

    return [lignesDatesVente, lignesDatesPresta];
  }

  /**
   * mise en forme des données pour correspondre aux 1/4 d'heure d'une journée
   * @param lignes les données à mettre en forme
   */
  public quartFilterDate(lignes: any) {
    // console.log(lignes);
    const lignesDatesVente: { [k: string]: any } = {};
    const lignesDatesPresta: { [k: string]: any } = {};
    const lignesDates: any = [];
    const endLignes: any = {};
    const labels = Object.keys(lignes);

    let start;
    let dateStart;
    let end;
    let dateEnd;
    if (labels.length === 0) {
      dateStart = moment().hour(0).minute(0);
      dateEnd = moment().hour(23).minutes(45);
    } else {
      start = labels[0].substr(0, 8);
      end = labels[labels.length - 1].substr(0, 8);
      dateStart = moment(start).hour(0).minute(0);
      dateEnd = moment(end).hour(23).minutes(45);
    }
    const quarts = dateEnd.diff(dateStart, "minutes") / 15;
    for (let q = 0; q <= quarts; q++) {
      const minutes = 15;
      let heure = dateStart.add(minutes, "minutes");
      const format = "yyyy-MM-ddTHH:mm:ss.000+0000";
      heure = heure.utc();
      const instant = heure.format("YYYY-MM-DDTHH:mm:ss.000+0000");
      let key = moment(instant).format("YYYYMMDD");
      if (q < 96) {
        key += q;
      } else {
        const eucl = Math.floor(q / 95);
        const qua = q - eucl * 95;
        key += qua;
      }
      // console.log(lignes[key]);
      if (lignes[key] === undefined) {
        // lignesDates[instant] = 0;
        lignesDatesVente[instant] = 0;
        lignesDatesPresta[instant] = 0;
      } else {
        lignesDatesVente[instant] = lignes[key][1];
        lignesDatesPresta[instant] = lignes[key][2];
      }
    }

    lignesDates.push(lignesDatesVente);
    lignesDates.push(lignesDatesPresta);

    return lignesDates;
  }
  /**
   * methode permettant de mettre en forme les lignes par quart sur mois
   * @param lignes les lignes à formatter
   * @returns le bon format
   */
  public numQuartMoisFilter(lignes: any) {
    const lignesDates = this.getData(lignes);
    return lignesDates;
  }
  /**
   * methode permettant de mettre en forme les lignes par ventilation
   * @param lignes les lignes à formatter
   * @returns le bon format
   */
  public numQuartVentil(lignes: any) {
    const lignesDates: { [k: string]: any } = {};
    const values = Object.keys(lignes);
    values.sort();

    for (const eq of values) {
      const ligne = lignes[eq];
      // console.log(eq, ligne);
      const data = this.getData(ligne);
      lignesDates[eq] = data;
    }

    // console.log(lignesDates);
    return lignesDates;
  }
  /**
   * transformation de données type date = [vente][presta] en deux dataset distinc
   * @param lignes - le lignes a transformer
   * @param colors - les couleurs a utiliser pour les dataset
   */
  public makeMeADataset(lignes: any, colors: any) {
    const data: { [k: string]: any } = {};
    data.labels = new Array();
    data.datasets = new Array();
    const labels = Object.keys(lignes[0]);
    const dataVente: Array<number> = [];
    const dataPresta: Array<number> = [];

    labels.forEach((label: string) => {
      if (label !== "") {
        data.labels.push(label);
        dataVente.push(lignes[0][label]);
        dataPresta.push(lignes[1][label]);
      }
    });

    const dataSetVente = new DataSet();
    dataSetVente.data = dataVente;
    dataSetVente.borderColor = colors[0].borderColor;
    dataSetVente.backgroundColor = colors[0].backgroundColor;
    dataSetVente.label = "Vente";

    const dataSetPresta = new DataSet();
    dataSetPresta.data = dataPresta;
    dataSetPresta.borderColor = colors[1].borderColor;
    dataSetPresta.backgroundColor = colors[1].backgroundColor;
    dataSetPresta.label = "Presta";

    data.datasets.push(dataSetVente);
    data.datasets.push(dataSetPresta);
    // data.labels = labels;

    return data;
  }
  /**
   * Converti les quarts d'heure reçu par l'API en heure valide(ligne par ligne)
   * ex: "52:00" recu par l'api = 52 x 15 = 700minutes -> 13h00
   */
  public convertQuartIndexToHour(label: string): string {
    const index = parseInt(label.split(":")[0], 10);
    const minutes = index * 15;

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    const padded = (n: number) => (n < 10 ? "0" + n : n);
    return `${padded(hours)}:${padded(mins)}`;
  }
  /**
   * Converti les quarts d'heure reçu par l'API en heure valide (pour un tableau)
   * ex: "52:00" recu par l'api = 52 x 15 = 700minutes -> 13h00
   */
  public convertQuartArrayToHour(data: {
    [index: string]: number[];
  }): [Record<string, number>, Record<string, number>] {
    const ventes: Record<string, number> = {};
    const prestas: Record<string, number> = {};

    for (const quartIndex in data) {
      if (!Object.prototype.hasOwnProperty.call(data, quartIndex)) {
        continue;
      }

      const values = data[quartIndex];
      if (!Array.isArray(values) || values.length < 3) {
        continue;
      }

      const index = parseInt(quartIndex, 10);
      const totalMinutes = index * 15;
      const hour = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      const timeKey = `${hour.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;

      ventes[timeKey] = values[1];
      prestas[timeKey] = values[2];
    }

    return [ventes, prestas];
  }

  /**
   * méthode de mise en forme des lignes en vente et prestation
   * @param lignes : les lignes à transformer
   * @returns les lignes tranformées en vente et presta
   */
  private getData(lignes: any) {
    let madate;
    // console.log(lignes);
    const lignesDates = new Array();
    const lignesDatesVente: { [k: string]: any } = {};
    const lignesDatesPresta: { [k: string]: any } = {};

    madate = moment().startOf("day");
    for (let q = 0; q < 96; q++) {
      const minutes = 15 * q;
      const key = moment()
        .startOf("day")
        .add(minutes, "minutes")
        .format("HH:mm");
      const returnDate = moment(madate)
        .startOf("day")
        .add(minutes, "minutes")
        .format("YYYY-MM-DDTHH:mm:ss");
      // console.log(key);
      if (lignes[key] === undefined) {
        lignesDatesVente[returnDate] = 0;
        lignesDatesPresta[returnDate] = 0;
      } else {
        lignesDatesVente[returnDate] = lignes[key][1];
        lignesDatesPresta[returnDate] = lignes[key][2];
      }
    }

    lignesDates.push(lignesDatesVente);
    lignesDates.push(lignesDatesPresta);
    // console.log(lignesDates);
    return lignesDates;
  }
}
